import { getSplit, type Split } from '../data/splits';
import { daysBetween, todayIso } from '../lib/dates';
import type { Settings } from '../store/types';

/**
 * Mesocycle bookkeeping.
 *
 * The program runs in blocks of `blockLengthWeeks`. In `auto` mode the split
 * alternates every block — antagonist, then push/pull/legs, then back again —
 * so the stimulus changes before your body stops responding to it, without you
 * having to decide anything. The last week of every Nth block is a deload:
 * same movements, roughly 60% of the sets, lighter loads. It is not a week off,
 * it is the week that lets the next block work.
 */
export interface BlockInfo {
  /** 1-based. Block 1 is the first week you start the program. */
  blockNumber: number;
  splitId: string;
  split: Split;
  /** 1-based week within the current block. */
  weekInBlock: number;
  blockLengthWeeks: number;
  isDeload: boolean;
  weeksRemainingInBlock: number;
  /** What the split switches to when this block ends. */
  nextSplitId: string;
  /** Multiply planned sets by this. 1 normally, 0.6 on a deload. */
  volumeMultiplier: number;
  /** Multiply suggested load by this. 1 normally, 0.9 on a deload. */
  loadMultiplier: number;
}

export const DELOAD_VOLUME_MULTIPLIER = 0.6;
export const DELOAD_LOAD_MULTIPLIER = 0.9;

function splitForBlockIndex(settings: Settings, blockIndex: number): string {
  if (settings.splitMode !== 'auto') return settings.splitMode;
  return blockIndex % 2 === 0 ? 'antagonist' : 'ppl';
}

export function blockInfo(settings: Settings, dateIso: string = todayIso()): BlockInfo {
  const length = Math.max(1, settings.blockLengthWeeks);
  const elapsedDays = daysBetween(settings.programStartDate, dateIso);
  // A date before the program started still belongs to week 1 of block 1.
  const weeksSinceStart = Math.max(0, Math.floor(elapsedDays / 7));

  const blockIndex = Math.floor(weeksSinceStart / length);
  const weekInBlock = (weeksSinceStart % length) + 1;

  const everyN = Math.max(0, settings.deloadEveryNBlocks);
  const isDeload =
    everyN > 0 && weekInBlock === length && (blockIndex + 1) % everyN === 0;

  const splitId = splitForBlockIndex(settings, blockIndex);

  return {
    blockNumber: blockIndex + 1,
    splitId,
    split: getSplit(splitId),
    weekInBlock,
    blockLengthWeeks: length,
    isDeload,
    weeksRemainingInBlock: length - weekInBlock,
    nextSplitId: splitForBlockIndex(settings, blockIndex + 1),
    volumeMultiplier: isDeload ? DELOAD_VOLUME_MULTIPLIER : 1,
    loadMultiplier: isDeload ? DELOAD_LOAD_MULTIPLIER : 1,
  };
}

/** Human-readable "where am I in the program" line for the Today screen. */
export function describeBlock(info: BlockInfo): string {
  const base = `Block ${info.blockNumber} · ${info.split.name} · week ${info.weekInBlock} of ${info.blockLengthWeeks}`;
  if (info.isDeload) return `${base} · deload`;
  return base;
}

export function describeBlockChange(info: BlockInfo): string | null {
  if (info.weeksRemainingInBlock > 0) return null;
  if (info.nextSplitId === info.splitId) return null;
  const next = getSplit(info.nextSplitId);
  return `Last week of this block — you switch to ${next.name} next week.`;
}
